import * as R from 'ramda';
import * as Yup from 'yup';
import { FieldArray } from 'formik';
import React, { Fragment } from 'react';
// components
import { TextComponent } from '../../../components/text';
import { FormSectionHeader } from '../../../components/form-section-header';
// features
import NormalizedCurrency from '../../../components/charge/normalized-currency';
// forms
import { Fieldset2 } from '../../../forms';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// icons
import * as I from '../../../svgs';
// ui
import { Box, Flex, StickedFlex } from '../../../ui';
// feature work-order
import { TotalComponent } from './invoices-totals';
//////////////////////////////////////////////////

const feesFieldNames = [GC.FIELD_NAME, GC.FIELD_COST];
const laborsFieldNames = [GC.FIELD_NAME, GC.FIELD_COST, GC.FIELD_QUANTITY];
const partsFieldNames = [GC.FIELD_NAME, GC.FIELD_PART_NUMBER, GC.FIELD_COST, GC.FIELD_QUANTITY];

const costsValidationSchema = {
  [GC.FIELD_COST]: G.yupNumberRequired,
  [GC.FIELD_NAME]: G.yupStringRequired,
  [GC.FIELD_QUANTITY]: G.yupNumberRequired,
  [GC.FIELD_PART_NUMBER]: G.yupStringRequired,
};

const getCostArrayValidationSchema = (fieldNames) =>
  Yup.array().of(Yup.object().shape(R.pick(fieldNames, costsValidationSchema)));

export const validationSchemaCostsObject = {
  [GC.FIELD_FEES]: getCostArrayValidationSchema(feesFieldNames),
  [GC.FIELD_PARTS]: getCostArrayValidationSchema(partsFieldNames),
  [GC.FIELD_LABORS]: getCostArrayValidationSchema(laborsFieldNames),
};

const defaultCostFields = {
  [GC.FIELD_COST]: '',
  [GC.FIELD_NAME]: '',
  [GC.FIELD_QUANTITY]: 1,
  [GC.FIELD_PART_NUMBER]: '',
};

const getNewCostFields = (fieldNames) =>
  R.assoc(GC.FIELD_ID, G.genShortId(), R.pick(fieldNames, defaultCostFields));

const costsInputWrapperStyles = { mr: 20, flexGrow: 1, flexBasis: 85 };

const costFieldSettings = {
  [GC.FIELD_NAME]: {
    type: 'text',
    isRequired: true,
    fieldName: GC.FIELD_NAME,
    label: ['titles:name', 'Name'],
    inputWrapperStyles: { mr: 20, width: 220 },
  },
  [GC.FIELD_COST]: {
    type: 'number',
    isRequired: true,
    fieldName: GC.FIELD_COST,
    label: ['titles:cost', 'Cost'],
    inputWrapperStyles: costsInputWrapperStyles,
  },
  [GC.FIELD_PART_NUMBER]: {
    type: 'text',
    isRequired: true,
    fieldName: GC.FIELD_PART_NUMBER,
    inputWrapperStyles: costsInputWrapperStyles,
    label: ['titles:part-number', 'Part Number'],
  },
  [GC.FIELD_QUANTITY]: {
    type: 'number',
    isRequired: true,
    fieldName: GC.FIELD_QUANTITY,
    label: ['titles:quantity', 'Quantity'],
    inputWrapperStyles: costsInputWrapperStyles,
  },
};

const costsInputWrapperStyles2 = { mr: 15, flexGrow: 1, flexBasis: 75 };

const costFieldSettings2 = {
  [GC.FIELD_NAME]: {
    type: 'text',
    isRequired: true,
    fieldName: GC.FIELD_NAME,
    label: ['titles:name', 'Name'],
    inputWrapperStyles: { mr: 15, width: 170 },
  },
  [GC.FIELD_COST]: {
    type: 'number',
    isRequired: true,
    fieldName: GC.FIELD_COST,
    label: ['titles:cost', 'Cost'],
    inputWrapperStyles: costsInputWrapperStyles2,
  },
  [GC.FIELD_PART_NUMBER]: {
    type: 'text',
    isRequired: true,
    fieldName: GC.FIELD_PART_NUMBER,
    label: ['titles:part-#', 'Part #'],
    inputWrapperStyles: costsInputWrapperStyles2,
  },
  [GC.FIELD_QUANTITY]: {
    type: 'number',
    isRequired: true,
    fieldName: GC.FIELD_QUANTITY,
    label: ['titles:quantity', 'Quantity'],
    inputWrapperStyles: { ...costsInputWrapperStyles2, flexBasis: 60 },
  },
};

const getFieldSettings = (fieldNames, version) => R.values(
  R.pick(fieldNames, R.equals(version, 2) ? costFieldSettings2 : costFieldSettings),
);

const CostRow = (props) => {
  const { values, fields, remove, itemIndex, arrayName, objectName, arrayLength, currencySymbol } = props;

  const names = G.isNilOrEmpty(objectName) ? [arrayName] : R.split('.', arrayName);

  const rowTotal = G.getCostTotal(
    R.pathOr(0, [...names, itemIndex, GC.FIELD_COST], values),
    R.pathOr(1, [...names, itemIndex, GC.FIELD_QUANTITY], values),
  );

  const rowTotalText = `${currencySymbol} ${rowTotal}`;

  return (
    <Flex mt={25} justifyContent='space-between'>
      <Flex>
        <Flex mr={10} cursor='pointer' onClick={() => remove(itemIndex)}>
          {I.trash(G.getTheme('colors.dark.blue'))}
        </Flex>
        <Fieldset2
          {...G.getFormikProps(props)}
          {...G.getArrayFormikProps(props)}
          fields={fields}
          arrayName={arrayName}
          arrayLength={arrayLength}
        />
      </Flex>
      <Flex width={115} flexShrink={0} justifyContent='flex-end'>
        {/* <Box
          mr='8px'
          fontWeight={700}
          textTransform='uppercase'
          color={G.getTheme('colors.darkGrey')}
        >
          {G.getWindowLocale('titles:total', 'Total')}:
        </Box> */}
        <TextComponent
          maxWidth={110}
          display='block'
          fontWeight={700}
          withEllipsis={true}
          title={rowTotalText}
        >
          {rowTotalText}
        </TextComponent>
      </Flex>
    </Flex>
  );
};

const CostFooter = ({ costs, currencySymbol }) => {
  const subTotal = G.mathRoundNumber(G.getCostsSubTotal(costs));
  const subTotalText = `${currencySymbol} ${subTotal}`;

  return (
    <Flex px={15} width='100%' flexShrink={0} justifyContent='flex-end'>
      <Box
        mr='8px'
        fontWeight={700}
        textTransform='uppercase'
        color={G.getTheme('colors.darkGrey')}
      >
        {G.getWindowLocale('titles:subtotal', 'Subtotal')}:
      </Box>
      <TextComponent
        maxWidth={300}
        display='block'
        fontWeight={700}
        withEllipsis={true}
        title={subTotalText}
      >
        {subTotalText}
      </TextComponent>
    </Flex>
  );
};

const CostsFieldArray = (props) => {
  const { name, title, values, version, objectName, fieldNames } = props;

  const arrayName = G.isNilOrEmpty(objectName) ? name : `${objectName}.${name}`;

  const costs = G.isNilOrEmpty(objectName) ?
    R.pathOr([], [name], values) :
    R.pathOr([], [...R.split('.', objectName), name], values);

  const currencySymbol = G.getCurrencySymbolFromRate(values);

  return (
    <FieldArray
      name={arrayName}
      render={(arrayHelpers) => (
        <FormSectionHeader
          expanded={true}
          title={G.getWindowLocale(...title)}
          action={() => arrayHelpers.push(getNewCostFields(fieldNames))}
        >
          {
            G.isNotNilAndNotEmpty(costs) &&
            <Fragment>
              <Box px={15} mb={15}>
                {
                  costs.map((_, index) => (
                    <CostRow
                      {...props}
                      {...arrayHelpers}
                      key={index}
                      itemIndex={index}
                      arrayName={arrayName}
                      arrayLength={R.length(costs)}
                      currencySymbol={currencySymbol}
                      fields={getFieldSettings(fieldNames, version)}
                    />
                  ))
                }
              </Box>
              <CostFooter costs={costs} currencySymbol={currencySymbol} />
            </Fragment>
          }
        </FormSectionHeader>
      )}
    />
  );
};

const NormalizedTotal = ({ normalizedTotal }) => {
  const { total, currency, exchangeDate } = normalizedTotal;

  const currencySymbol = G.getCurrencySymbol(currency);

  return (
    <TotalComponent
      mr={15}
      maxWidth={270}
      totalText={`${G.getWindowLocale('titles:normalized-total', 'Normalized Total')}: ${
        currencySymbol} ${total} (${exchangeDate})`}
    />
  );
};

const CostsSectionFooter = ({ values, version, objectName, isEditMode, additionTotal }) => {
  const normalizedTotal = G.isNotNilAndNotEmpty(objectName)
    ? R.path([...R.split('.', objectName), GC.FIELD_NORMALIZED_TOTAL], values) : G.getPropFromObject(GC.FIELD_NORMALIZED_TOTAL, values);

  const costsTotal = G.isNotNilAndNotEmpty(objectName)
    ? G.getCostsTotal(R.path(R.split('.', objectName), values)) : G.getCostsTotal(values);

  const currency = G.isNotNilAndNotEmpty(objectName)
    ? R.path([...R.split('.', objectName), GC.FIELD_CURRENCY], values) : G.getPropFromObject(GC.FIELD_CURRENCY, values);

  const totalToUse = G.mathRoundNumber(R.add(R.or(additionTotal, 0), costsTotal));

  const currencySymbol = G.getCurrencySymbol(currency);

  const showNormalizedTotal = G.isAllTrue(
    isEditMode,
    G.isNotNilAndNotEmpty(normalizedTotal),
    R.not(R.pathEq(currency, [GC.FIELD_CURRENCY], normalizedTotal)),
  );

  return (
    <StickedFlex
      mt={10}
      p='5px'
      bottom='0px'
      width='100%'
      justifyContent='center'
      bg={G.getTheme('colors.whiteGrey')}
      borderColor={G.getTheme('colors.light.darkGrey')}
      borderBottom={R.equals(version, 2) ? null : '1px solid'}
    >
      {
        showNormalizedTotal && <NormalizedTotal normalizedTotal={normalizedTotal} />
      }
      <TotalComponent
        maxWidth={150}
        totalText={`${G.getWindowLocale('titles:total', 'Total')}: ${currencySymbol} ${totalToUse}`}
      />
      <NormalizedCurrency
        p='0 0 0 15px'
        total={totalToUse}
        sourceCurrency={currency}
        title={G.getWindowLocale('titles:current-normalized', 'Current Normalized')}
      />
    </StickedFlex>
  );
};

const costsSettings = [
  {
    name: GC.FIELD_LABORS,
    fieldNames: laborsFieldNames,
    title: ['titles:labor', 'Labor'],
  },
  {
    name: GC.FIELD_PARTS,
    fieldNames: partsFieldNames,
    title: ['titles:parts', 'Parts'],
  },
  {
    name: GC.FIELD_FEES,
    fieldNames: feesFieldNames,
    title: ['titles:fees', 'Fees'],
  },
];

export const CostsSection = (props) => (
  <Fragment>
    {
      costsSettings.map((setting) => (
        <CostsFieldArray {...props} {...setting} key={G.getPropFromObject(GC.FIELD_NAME, setting)} />
      ))
    }
    <CostsSectionFooter {...props} />
  </Fragment>
);
